import { lineRadial, scaleLinear, curveCatmullRomClosed } from 'd3';
import type { Signature } from '../lib/types';
import { describeRatio, formatRatio } from '../lib/signature';

interface Props { signature: Signature }

const axes = [
  ['Calm baseline', 'baseline'] as const,
  ['Signal pressure', 'signal'] as const,
  ['Flow thread', 'flow'] as const,
  ['Oddball field', 'oddball'] as const
];

export function SignatureChart({ signature }: Props) {
  const values = axes.map(([, key]) => signature[key]);
  const radius = scaleLinear().domain([0.72, 1.38]).range([34, 82]).clamp(true);
  const pts = values.map((value, i) => [((Math.PI * 2) / values.length) * i, radius(value)] as [number, number]);
  const path = lineRadial<[number, number]>()
    .angle((d) => d[0])
    .radius((d) => d[1])
    .curve(curveCatmullRomClosed.alpha(0.75))(pts) || '';

  return (
    <div className="signature-card">
      <div>
        <p className="kicker">Time-distortion signature</p>
        <h2>{signature.archetype}</h2>
        <p>{signature.note}</p>
      </div>
      <svg viewBox="-112 -112 224 224" className="signature-chart" role="img" aria-label="Radial chart of time distortion by condition">
        <defs>
          <radialGradient id="sigFill" cx="50%" cy="50%" r="65%">
            <stop offset="0%" stopColor="#7cf7ff" stopOpacity="0.75" />
            <stop offset="100%" stopColor="#8d6bff" stopOpacity="0.16" />
          </radialGradient>
        </defs>
        {[34, 58, 82].map((r) => <circle key={r} cx="0" cy="0" r={r} fill="none" stroke="currentColor" strokeOpacity="0.12" />)}
        {axes.map(([label], i) => {
          const a = ((Math.PI * 2) / axes.length) * i - Math.PI / 2;
          const x = Math.cos(a) * 96;
          const y = Math.sin(a) * 96;
          return (
            <g key={label}>
              <line x1="0" y1="0" x2={Math.cos(a) * 88} y2={Math.sin(a) * 88} stroke="currentColor" strokeOpacity="0.12" />
              <text x={x} y={y} textAnchor="middle" dominantBaseline="middle">{label}</text>
            </g>
          );
        })}
        <path d={path} transform="rotate(-90)" fill="url(#sigFill)" stroke="#7cf7ff" strokeWidth="2.5" />
        {pts.map(([angle, r], i) => (
          <circle key={i} cx={Math.cos(angle - Math.PI / 2) * r} cy={Math.sin(angle - Math.PI / 2) * r} r="3.6" fill="#ffef8a" />
        ))}
      </svg>
      <div className="metric-grid">
        {axes.map(([label, key]) => (
          <div key={key} className="metric">
            <span>{label}</span>
            <strong>{formatRatio(signature[key])}</strong>
            <small>{describeRatio(signature[key])}</small>
          </div>
        ))}
        <div className="metric hot">
          <span>Flow compression</span>
          <strong>{Math.round(signature.flowCompressionIndex * 100)} pts</strong>
          <small>baseline minus flow thread</small>
        </div>
        <div className="metric hot">
          <span>Elasticity</span>
          <strong>{Math.round(signature.elasticity * 1000) / 10}%</strong>
          <small>variance across conditions</small>
        </div>
      </div>
    </div>
  );
}
